import {
  Panel,
  Stack,
  LocalizedString,
  useIsMobileLayout,
  Box,
  Card,
  Heading,
  Space,
} from "@dbn/omnichannel-design-system-g4p";
import { pipe, constTrue } from "fp-ts/function";
import { option, set, array, eq } from "fp-ts";
import { PreContractualDocumentMeta } from "../documentAPI";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { NonEmptyString } from "io-ts-types/lib/NonEmptyString";
import { DocumentItem, DocumentReadingConfirmProps } from "./DocumentItem";
import { getDocumentReadingStatus, getFirstUnreadDocument } from "./utils";
import { TaskEither } from "fp-ts/TaskEither";
import { ContentWrapper } from "../ContentWrapper";

export type ReadingConfirmProps =
  | {
      readingConfirmationType: "none";
    }
  | {
      readingConfirmationType: "status";
      documentsRead: Set<NonEmptyString>;
    }
  | {
      readingConfirmationType: "checkbox";
      documentsRead: Set<NonEmptyString>;
      onDocumentRead: (documentId: NonEmptyString) => TaskEither<unknown, void>;
      onDocumentUnread: (
        documentId: NonEmptyString
      ) => TaskEither<unknown, void>;
    };

type Props = {
  documents: NonEmptyArray<PreContractualDocumentMeta>;
  documentToCheckForReadingConfirmation?:
    | NonEmptyArray<PreContractualDocumentMeta>
    | undefined;
  showErrors: boolean;
  isSigningStarted?: boolean;
  isForeignClient?: boolean;
  onDocumentOpen: (document: PreContractualDocumentMeta) => unknown;
  onForeignDocumentOpen: (document: PreContractualDocumentMeta) => unknown;
  title: LocalizedString;
  ariaLabel?: LocalizedString;
  variant?: string;
} & ReadingConfirmProps;

export function ContractsPanel(props: Props) {
  const isMobileLayout = useIsMobileLayout();

  function getDocumentReadingConfirmProps(
    docId: NonEmptyString
  ): DocumentReadingConfirmProps {
    switch (props.readingConfirmationType) {
      case "none":
        return { readingConfirmationType: "none" };
      case "status":
        return {
          readingConfirmationType: "status",
          readStatus: getDocumentReadingStatus(
            props.documents,
            props.documentsRead
          )(docId),
        };
      case "checkbox":
        const isRead = pipe(props.documentsRead, set.elem(eq.eqStrict)(docId));
        return {
          readingConfirmationType: "checkbox",
          isRead,
          onRead: props.onDocumentRead(docId),
          onUnread: props.onDocumentUnread(docId),
          disabled:
            !isRead &&
            pipe(
              getFirstUnreadDocument(
                props.documentToCheckForReadingConfirmation
                  ? props.documentToCheckForReadingConfirmation
                  : props.documents,
                props.documentsRead
              ),
              option.fold(constTrue, d => d.docId !== docId)
            ),
        };
    }
  }

  return isMobileLayout ? (
    <Card>
      <Box column shrink style={{ paddingLeft: 5, paddingRight: 5 }}>
        <Heading size={"small"} weight={"medium"} children={props.title} />
        <Space units={4} />
        <Stack column units={16} divider label={props.title}>
          {pipe(
            props.documents,
            array.mapWithIndex((index, document) => (
              <ContentWrapper>
                <DocumentItem
                  variant={props.variant}
                  document={document}
                  dataTestId={`contract_${index + 1}`}
                  key={document.docId}
                  showErrors={props.showErrors}
                  {...getDocumentReadingConfirmProps(document.docId)}
                  onDocumentOpen={props.onDocumentOpen}
                  onForeignDocumentOpen={props.onForeignDocumentOpen}
                  isForeignClient={props.isForeignClient}
                />
              </ContentWrapper>
            ))
          )}
        </Stack>
      </Box>
    </Card>
  ) : (
    <Panel
      stripSurroundingPadding={isMobileLayout}
      headerIcon={option.none}
      isOpen={true}
      title={props.title}
      status={props.isSigningStarted ? "disabled" : "active"}
      onSelect={option.none}
      {...(props.ariaLabel ? { ariaLabel: props.ariaLabel } : {})}
      content={() => (
        <Stack column units={16} divider>
          {pipe(
            props.documents,
            array.mapWithIndex((index, document) => (
              <DocumentItem
                variant={props.variant}
                document={document}
                dataTestId={`contract_${index + 1}`}
                key={document.docId}
                showErrors={props.showErrors}
                {...getDocumentReadingConfirmProps(document.docId)}
                onDocumentOpen={props.onDocumentOpen}
                onForeignDocumentOpen={props.onForeignDocumentOpen}
                isForeignClient={props.isForeignClient}
              />
            ))
          )}
        </Stack>
      )}
    />
  );
}
